import { useEffect, useState } from "react";
import {
  useOutletContext,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { Heading } from "V2.0/common/heading";
import { Button } from "V2.0/common/button";

import usePipelineV2 from "../usePipelineV2";
import usePipelineV2RunsPaginated from "./usePipelineV2RunsPaginated";

import PipelineV2RunsLoading from "./PipelineV2RunsLoading";
import PipelineV2RunsEmpty from "./PipelineV2RunsEmpty";
import PipelineV2RunPreview from "../PipelineV2RunPreview";
import PipelineV2RunsList from "./PipelineV2RunsList";
import PipelineV2RunDelete from "./PipelineV2RunDelete";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

export default PipelineRunsV2;

function PipelineRunsV2() {
  const { projectId: workspaceId } = useOutletContext<{ projectId: string }>();
  const { id: pipelineId } = useParams();
  const { pipeline, isLoading, getPipeline } = usePipelineV2();
  const {
    pipelineV2Runs,
    isPipelineV2RunsLoading,
    removePipelineV2Run,
    fetchPaginatedPipelineV2Runs,
  } = usePipelineV2RunsPaginated(workspaceId, pipelineId);
  const navigate = useNavigate();
  const location = useLocation();

  const [viewPipelineV2Run, setViewPipelineV2Run] = useState(undefined);
  const [deletePipelineV2Run, setDeletePipelineV2Run] = useState(undefined);

  useEffect(() => {
    if (workspaceId && pipelineId) {
      getPipeline(workspaceId, pipelineId);
      fetchPaginatedPipelineV2Runs({ page: 1, pageSize: 10 }, true);
    }
  }, [workspaceId, pipelineId]);

  const handlePageChange = (page) => {
    fetchPaginatedPipelineV2Runs({ page }, true);
  };

  const isPipelineV2RunsEmpty = pipelineV2Runs.pipelineRuns.length === 0;

  const pipelineRunHeading = isLoading ? "Pipeline Runs" : pipeline.name;

  return (
    <>
      <div className="max-w-6xl mx-auto flex flex-col flex-1 h-full gap-y-8 px-4 py-8">
        <div className="flex items-center gap-x-2">
          <Button
            plain
            onClick={() => {
              navigate({ pathname: "/pipelines/v2", search: location.search });
            }}
          >
            <ArrowLeftIcon />
          </Button>
          <Heading>{pipelineRunHeading}</Heading>
        </div>

        <div className="h-full w-full min-h-0 flex-1 flex flex-col overflow-auto">
          {isPipelineV2RunsLoading && <PipelineV2RunsLoading />}
          {!isPipelineV2RunsLoading && isPipelineV2RunsEmpty && (
            <PipelineV2RunsEmpty />
          )}
          {!isPipelineV2RunsEmpty && !isPipelineV2RunsLoading && (
            <PipelineV2RunsList
              pipelineRuns={pipelineV2Runs.pipelineRuns}
              pagination={pipelineV2Runs.pagination}
              onPipelineRunClick={(pipelineRun) =>
                setViewPipelineV2Run(pipelineRun)
              }
              onPipelineRunDelete={(pipelineRun) =>
                setDeletePipelineV2Run(pipelineRun)
              }
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
      {viewPipelineV2Run && (
        <PipelineV2RunPreview
          workspaceId={workspaceId}
          pipelineRun={viewPipelineV2Run}
          onClose={() => setViewPipelineV2Run(undefined)}
        />
      )}
      {deletePipelineV2Run && (
        <PipelineV2RunDelete
          pipelineRun={deletePipelineV2Run}
          onDeletePipelineRun={removePipelineV2Run}
          onClose={() => setDeletePipelineV2Run(undefined)}
        />
      )}
    </>
  );
}
